import { useState } from 'react';
import { StanCalendar } from '@/components';
import { type DateRange } from 'react-day-picker';

export default function InternalsCalendarPage() {
    // Calendar state variables - practical examples
    const [eventDate, setEventDate] = useState<Date | undefined>(new Date());
    const [vacationRange, setVacationRange] = useState<DateRange | undefined>();
    const [appointmentDate, setAppointmentDate] = useState<Date | undefined>();
    const [meetingDates, setMeetingDates] = useState<Date[] | undefined>([]);
    const [deadlineDate, setDeadlineDate] = useState<Date | undefined>();
    const [birthdayDate, setBirthdayDate] = useState<Date | undefined>();

    return (
        <div className='space-y-8'>
            {/* Calendar Section */}
            <div
                className='rounded-lg p-8 shadow-sm'
                style={{
                    border: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-bg-canvas)',
                }}
            >
                <div className='mb-8'>
                    <h2 className='text-2xl font-semibold' style={{ color: 'var(--text-color-dark)' }}>
                        Calendar
                    </h2>
                    <p className='mt-2' style={{ color: 'var(--text-color-gray)' }}>
                        Date picker components for common real-world scenarios
                    </p>
                </div>

                <div className='space-y-12'>
                    <div className='grid grid-cols-1 gap-8 lg:grid-cols-2 xl:grid-cols-3'>
                        {/* 1. Event Date Picker */}
                        <div className='space-y-4'>
                            <div className='text-center'>
                                <h4 className='text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                    Event Date
                                </h4>
                                <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                    Schedule an event or appointment
                                </p>
                            </div>
                            <div className='flex justify-center'>
                                <div
                                    className='rounded-lg p-4'
                                    style={{
                                        border: '1px solid var(--color-border-subtle)',
                                        backgroundColor: 'var(--color-bg-subtle)',
                                    }}
                                >
                                    <StanCalendar
                                        analyticsName='event-date-picker'
                                        mode='single'
                                        selected={eventDate}
                                        onSelect={setEventDate}
                                        disabled={{ before: new Date() }}
                                    />
                                </div>
                            </div>
                            {eventDate && (
                                <div className='rounded-md p-3' style={{ backgroundColor: 'var(--color-info-subtle)' }}>
                                    <p className='text-center text-sm' style={{ color: 'var(--text-color-info-bold)' }}>
                                        Event: {eventDate.toLocaleDateString()}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* 2. Vacation Range Picker */}
                        <div className='space-y-4'>
                            <div className='text-center'>
                                <h4 className='text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                    Vacation Dates
                                </h4>
                                <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                    Select start and end dates for time off
                                </p>
                            </div>
                            <div className='flex justify-center'>
                                <div
                                    className='rounded-lg p-4'
                                    style={{
                                        border: '1px solid var(--color-border-subtle)',
                                        backgroundColor: 'var(--color-bg-subtle)',
                                    }}
                                >
                                    <StanCalendar
                                        analyticsName='vacation-range-picker'
                                        mode='range'
                                        selected={vacationRange}
                                        onSelect={setVacationRange}
                                        disabled={{ before: new Date() }}
                                    />
                                </div>
                            </div>
                            {vacationRange?.from && (
                                <div
                                    className='rounded-md p-3'
                                    style={{ backgroundColor: 'var(--color-success-subtle)' }}
                                >
                                    <p
                                        className='text-center text-sm'
                                        style={{ color: 'var(--text-color-success-bold)' }}
                                    >
                                        {vacationRange.to
                                            ? `${vacationRange.from.toLocaleDateString()} - ${vacationRange.to.toLocaleDateString()}`
                                            : `Start: ${vacationRange.from.toLocaleDateString()}`}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* 3. Business Appointment */}
                        <div className='space-y-4'>
                            <div className='text-center'>
                                <h4 className='text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                    Business Meeting
                                </h4>
                                <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                    Weekdays only, future dates
                                </p>
                            </div>
                            <div className='flex justify-center'>
                                <div
                                    className='rounded-lg p-4'
                                    style={{
                                        border: '1px solid var(--color-border-subtle)',
                                        backgroundColor: 'var(--color-bg-subtle)',
                                    }}
                                >
                                    <StanCalendar
                                        analyticsName='business-appointment'
                                        mode='single'
                                        selected={appointmentDate}
                                        onSelect={setAppointmentDate}
                                        disabled={[{ before: new Date() }, { dayOfWeek: [0, 6] }]}
                                    />
                                </div>
                            </div>
                            {appointmentDate && (
                                <div
                                    className='rounded-md p-3'
                                    style={{ backgroundColor: 'var(--color-notice-subtle)' }}
                                >
                                    <p
                                        className='text-center text-sm'
                                        style={{ color: 'var(--text-color-notice-bold)' }}
                                    >
                                        Meeting: {appointmentDate.toLocaleDateString()}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* 4. Team Meeting Dates */}
                        <div className='space-y-4'>
                            <div className='text-center'>
                                <h4 className='text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                    Team Meetings
                                </h4>
                                <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                    Select multiple meeting dates
                                </p>
                            </div>
                            <div className='flex justify-center'>
                                <div
                                    className='rounded-lg p-4'
                                    style={{
                                        border: '1px solid var(--color-border-subtle)',
                                        backgroundColor: 'var(--color-bg-subtle)',
                                    }}
                                >
                                    <StanCalendar
                                        analyticsName='team-meetings'
                                        mode='multiple'
                                        selected={meetingDates}
                                        onSelect={setMeetingDates}
                                        disabled={{ before: new Date() }}
                                        max={5}
                                    />
                                </div>
                            </div>
                            {meetingDates && meetingDates.length > 0 && (
                                <div
                                    className='rounded-md p-3'
                                    style={{ backgroundColor: 'var(--color-warning-subtle)' }}
                                >
                                    <p
                                        className='text-center text-sm'
                                        style={{ color: 'var(--text-color-warning-bold)' }}
                                    >
                                        {meetingDates.length} meeting{meetingDates.length > 1 ? 's' : ''} scheduled
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* 5. Project Deadline */}
                        <div className='space-y-4'>
                            <div className='text-center'>
                                <h4 className='text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                    Project Deadline
                                </h4>
                                <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                    Set a future deadline date
                                </p>
                            </div>
                            <div className='flex justify-center'>
                                <div
                                    className='rounded-lg p-4'
                                    style={{
                                        border: '1px solid var(--color-border-subtle)',
                                        backgroundColor: 'var(--color-bg-subtle)',
                                    }}
                                >
                                    <StanCalendar
                                        analyticsName='project-deadline'
                                        mode='single'
                                        selected={deadlineDate}
                                        onSelect={setDeadlineDate}
                                        disabled={{ before: new Date() }}
                                        captionLayout='dropdown'
                                    />
                                </div>
                            </div>
                            {deadlineDate && (
                                <div
                                    className='rounded-md p-3'
                                    style={{ backgroundColor: 'var(--color-negative-subtle)' }}
                                >
                                    <p
                                        className='text-center text-sm'
                                        style={{ color: 'var(--text-color-negative-bold)' }}
                                    >
                                        Deadline: {deadlineDate.toLocaleDateString()}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* 6. Birthday Picker */}
                        <div className='space-y-4'>
                            <div className='text-center'>
                                <h4 className='text-lg font-medium' style={{ color: 'var(--text-color-secondary)' }}>
                                    Birthday
                                </h4>
                                <p className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                    Select any date (past or future)
                                </p>
                            </div>
                            <div className='flex justify-center'>
                                <div
                                    className='rounded-lg p-4'
                                    style={{
                                        border: '1px solid var(--color-border-subtle)',
                                        backgroundColor: 'var(--color-bg-subtle)',
                                    }}
                                >
                                    <StanCalendar
                                        analyticsName='birthday-picker'
                                        mode='single'
                                        selected={birthdayDate}
                                        onSelect={setBirthdayDate}
                                        captionLayout='dropdown'
                                        showOutsideDays
                                    />
                                </div>
                            </div>
                            {birthdayDate && (
                                <div
                                    className='rounded-md p-3'
                                    style={{ backgroundColor: 'var(--color-critical-subtle)' }}
                                >
                                    <p
                                        className='text-center text-sm'
                                        style={{ color: 'var(--text-color-critical-bold)' }}
                                    >
                                        Birthday: {birthdayDate.toLocaleDateString()}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
